import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Card,
  CardContent,
  CardActionArea,
  Button,
  AppBar,
  Toolbar,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Collapse,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  SportsMma as SportsIcon,
  Announcement as AnnouncementIcon,
  PushPin as PushPinIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  LocationOn as LocationOnIcon,
  ArrowForward as ArrowForwardIcon,
  Login as LoginIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
// [Web UX 1. csomag (4.3), 2026-10-03] a `publishedDate` UTC-ben, „Z” nélkül jön → közös segéd (budapesti idő).
import { formatServerDateLong } from '../utils/dateUtils';
import { API_BASE_URL } from '../utils/config';
import { responseErrorMessage } from '../utils/apiErrors';
import ThemeToggle from './ThemeToggle';

interface PublicNews {
  newsId: string;
  title: string;
  summary: string;
  content: string;
  publishedDate: string;
  isPinned: boolean;
}

const venueNames = [
  { name: 'Aréna Törökbálint', address: '2045 Törökbálint, Torbágy utca 1.' },
  { name: 'MKOSZ Tornacsarnok Budaörs', address: '2040 Budaörs, Liget utca 12.' },
  { name: 'Budakeszi Asztalitenisz SE', address: '2092 Budakeszi, Árpád fejedelem tér 1.' },
  { name: 'BIT Biatorbágy', address: '2051 Biatorbágy, Antall József utca 1.' },
  { name: 'Érdi Teleki Sámuel Ált. Iskola', address: '2030 Érd, Burkoló utca 40.' },
];

interface PublicLandingProps {
  onNavigateToLogin: () => void;
  onNavigateToRegister: () => void;
}

const PublicLanding: React.FC<PublicLandingProps> = ({ onNavigateToLogin, onNavigateToRegister }) => {
  const [news, setNews] = useState<PublicNews[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedNews, setExpandedNews] = useState<string[]>([]);

  useEffect(() => {
    fetchPublicNews();
  }, []);

  const fetchPublicNews = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/news/public?limit=20`);
      
      // [Web UX 3. csomag (D), 2026-10-04] a `!ok` válasz a szerver magyar indoklását / „<tartalék> (HTTP n)"-t adja
      // (eddig egy angol `throw new Error('Failed to fetch news')` után a catch FIX szövege ment ki, az indoklás — pl. a
      // sebességkorlát 429-es magyar mondata — elveszett). Nyilvános, token nélküli hívás: munkamenet-zár nincs, ezért a
      // sima `responseErrorMessage`. A catch-ág (csak hálózati bukás / hibás JSON) szándékosan fix magyar szöveg marad.
      if (!response.ok) {
        const body = await response.text().catch(() => '');
        setError(responseErrorMessage(body, response.status, 'Nem sikerült betölteni a híreket'));
        return;
      }
      
      const data = await response.json();
      setNews(data);
    } catch (err) {
      setError('Nem sikerült betölteni a híreket');
      // Only log fetch errors in development
      if (process.env.NODE_ENV === 'development') {
        console.error(err);
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleNewsExpansion = (newsId: string) => {
    setExpandedNews(prev =>
      prev.includes(newsId)
        ? prev.filter(id => id !== newsId)
        : [...prev, newsId]
    );
  };

  const pinnedNews = news.filter(n => n.isPinned);
  const regularNews = news.filter(n => !n.isPinned);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <SportsIcon sx={{ mr: 1 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            BK Pingpong
            <Typography component="span" variant="caption" sx={{ ml: 1, opacity: 0.8, display: { xs: 'none', sm: 'inline' } }}>
              – Az oldal fejlesztés alatt, hamarosan elérhetővé válik...
            </Typography>
          </Typography>
          <ThemeToggle />
          <Button
            color="inherit"
            startIcon={<PersonAddIcon />}
            onClick={onNavigateToRegister}
            sx={{ ml: 0.5, minWidth: 'auto', px: { xs: 1, sm: 2 } }}
            size="small"
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Regisztráció</Box>
          </Button>
          <Button
            color="inherit"
            startIcon={<LoginIcon />}
            onClick={onNavigateToLogin}
            sx={{ ml: 0.5, minWidth: 'auto', px: { xs: 1, sm: 2 } }}
            size="small"
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Bejelentkezés</Box>
          </Button>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #00695C 0%, #004D47 50%, #1A4C4A 100%)',
          color: 'white',
          py: 8,
          mb: 4,
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h2" component="h1" gutterBottom fontWeight="bold">
            Üdvözöljük a Buda Környéki Pingpong Klubnál!
          </Typography>
          <Typography variant="h5" sx={{ mb: 3, opacity: 0.95 }}>
            Budapest egyik legaktívabb asztalitenisz közössége
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, opacity: 0.9 }}>
            Csatlakozzon hozzánk edzéseinken, versenyeinken és közösségi eseményeinken.
            Minden korosztály és tudásszint számára biztosítunk lehetőségeket.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              sx={{ bgcolor: 'white', color: 'primary.main' }}
              startIcon={<LoginIcon />}
              onClick={onNavigateToLogin}
            >
              Bejelentkezés
            </Button>
            <Button
              variant="contained"
              size="large"
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
              startIcon={<PersonAddIcon />}
              onClick={onNavigateToRegister}
            >
              Regisztráció
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Venues Section */}
      <Container maxWidth="lg" sx={{ mb: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" component="h2" gutterBottom>
            <LocationOnIcon sx={{ verticalAlign: 'middle', mr: 2 }} />
            Helyszíneink
          </Typography>
          <Divider />
        </Box>
        <Grid container spacing={2}>
          {venueNames.map((venue) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={venue.name}>
              <Card
                elevation={2}
                component={Link}
                to="/helyszinek"
                sx={{
                  height: '100%',
                  textDecoration: 'none',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 },
                }}
              >
                <CardActionArea sx={{ height: '100%', p: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                    {venue.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <LocationOnIcon fontSize="small" />
                    {venue.address}
                  </Typography>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Button
            component={Link}
            to="/helyszinek"
            variant="outlined"
            endIcon={<ArrowForwardIcon />}
          >
            Összes helyszín és edzésidőpont
          </Button>
        </Box>
      </Container>

      {/* News Section */}
      <Container maxWidth="lg" sx={{ pb: 6 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h2" gutterBottom>
            <AnnouncementIcon sx={{ verticalAlign: 'middle', mr: 2 }} />
            Hírek és Közlemények
          </Typography>
          <Divider />
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && news.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              Jelenleg nincsenek hírek.
            </Typography>
          </Paper>
        )}

        {/* Pinned News */}
        {pinnedNews.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PushPinIcon color="warning" />
              Kiemelt hírek
            </Typography>
            <Grid container spacing={3}>
              {pinnedNews.map((item) => (
                <Grid size={12} key={item.newsId}>
                  <Card
                    elevation={3}
                    sx={{
                      border: '2px solid',
                      borderColor: 'warning.main',
                      bgcolor: 'rgba(255, 193, 7, 0.05)',
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h5" component="h3" gutterBottom>
                            {item.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {formatServerDateLong(item.publishedDate, { withTime: true })}
                          </Typography>
                        </Box>
                        <Chip
                          icon={<PushPinIcon />}
                          label="Kiemelt"
                          color="warning"
                          size="small"
                        />
                      </Box>
                      
                      <Typography variant="body1" paragraph>
                        {item.summary}
                      </Typography>
                      
                      <Collapse in={expandedNews.includes(item.newsId)}>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {item.content}
                        </Typography>
                      </Collapse>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <Button
                          onClick={() => toggleNewsExpansion(item.newsId)}
                          endIcon={expandedNews.includes(item.newsId) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        >
                          {expandedNews.includes(item.newsId) ? 'Kevesebb' : 'Teljes hír'}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Regular News */}
        {regularNews.length > 0 && (
          <Grid container spacing={3}>
            {regularNews.map((item) => (
              <Grid size={{ xs: 12, md: 6 }} key={item.newsId}>
                <Card elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flex: 1 }}>
                    <Typography variant="h6" component="h3" gutterBottom>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {formatServerDateLong(item.publishedDate)}
                    </Typography>
                    <Typography variant="body2" paragraph>
                      {item.summary}
                    </Typography>
                    
                    <Collapse in={expandedNews.includes(item.newsId)}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {item.content}
                      </Typography>
                    </Collapse>
                  </CardContent>
                  
                  <Box sx={{ p: 2, pt: 0 }}>
                    <Button
                      size="small"
                      onClick={() => toggleNewsExpansion(item.newsId)}
                      endIcon={expandedNews.includes(item.newsId) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    >
                      {expandedNews.includes(item.newsId) ? 'Kevesebb' : 'Teljes hír'}
                    </Button>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          bgcolor: 'primary.dark',
          color: 'white',
          py: 4,
          mt: 6,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h6" gutterBottom>
                Elérhetőségek
              </Typography>
              <Typography variant="body2">
                Hajduska Rita: 06 70 40 96 996
              </Typography>
              <Typography variant="body2">
                Email: pingpongozzunk@gmail.com
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <a
                  href="https://www.facebook.com/pingpongHajduskaRita/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'white', textDecoration: 'underline' }}
                >
                  Facebook: pingpongHajduskaRita
                </a>
              </Typography>
            </Grid>
          </Grid>
          <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.2)' }} />
          <Typography variant="body2" align="center">
            © {new Date().getFullYear()} BK Pingpong. Minden jog fenntartva.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default PublicLanding;